import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { blogPosts } from '@/mocks/blogs';
import { useSEO } from '@/hooks/useSEO';
import ScrollBatteryBar from '@/components/feature/ScrollBatteryBar';
import SiteHeader from '@/components/feature/SiteHeader';
import Footer from '@/pages/home/components/Footer';
import GlowButton from '@/components/base/GlowButton';

const blogCategories = ['All', 'Growth', 'Tools', 'Rankings', 'Monetization', 'Trends', 'Management'];

const adSlots = [
  {
    id: 'ad1',
    image: 'https://readdy.ai/api/search-image?query=minimalist%20frosted%20glass%20abstract%20soft%20pale%20blue%20white%20mist%20translucent%20overlapping%20circle%20shapes%20subtle%20gradient%20clean%20light%20background%20serene%20elegant%20modern%20art%20small%20panel&width=256&height=160&seq=bl401&orientation=landscape',
    title: 'Promote Your Community',
    desc: 'Reach 50k+ community seekers daily.',
    cta: 'Get Started',
  },
  {
    id: 'ad2',
    image: 'https://readdy.ai/api/search-image?query=minimalist%20frosted%20glass%20abstract%20soft%20mint%20green%20white%20mist%20translucent%20wave%20layered%20shapes%20subtle%20gradient%20clean%20light%20background%20elegant%20modern%20art%20small%20panel&width=256&height=160&seq=bl402&orientation=landscape',
    title: 'TG Bot Pro',
    desc: 'Automate moderation & grow faster.',
    cta: 'Try Free',
  },
  {
    id: 'ad3',
    image: 'https://readdy.ai/api/search-image?query=minimalist%20frosted%20glass%20abstract%20soft%20amber%20ivory%20white%20mist%20translucent%20geometric%20prism%20shapes%20subtle%20gradient%20clean%20light%20background%20elegant%20modern%20art%20small%20panel&width=256&height=160&seq=bl403&orientation=landscape',
    title: 'Analytics Suite',
    desc: 'Track member growth & engagement.',
    cta: 'Learn More',
  },
];

function FeaturedCarousel({ onNavigate }: { onNavigate: (id: string) => void }) {
  const featured = blogPosts.filter((p) => p.featured).concat(blogPosts.slice(2, 4));
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setCurrent((c) => (c + 1) % featured.length), [featured.length]);
  const prev = () => setCurrent((c) => (c - 1 + featured.length) % featured.length);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 4500);
    return () => clearInterval(t);
  }, [paused, next]);

  const post = featured[current];

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const formatViews = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`;

  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden mb-6 cursor-pointer group"
      style={{ height: '400px' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onClick={() => onNavigate(post.id)}
    >
      {featured.map((p, i) => (
        <div
          key={p.id}
          className="absolute inset-0 transition-opacity duration-700"
          style={{ opacity: i === current ? 1 : 0 }}
        >
          <img src={p.coverImage} alt={p.title} className="w-full h-full object-cover object-top" />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/88 via-slate-900/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/45 via-transparent to-transparent" />

      {/* Top-right stats */}
      <div className="absolute top-4 right-5 flex items-center gap-3">
        <div className="flex items-center gap-1.5 bg-black/30 backdrop-blur-sm rounded-full px-2.5 py-1 border border-white/15">
          <i className="ri-eye-line text-white/70 text-xs"></i>
          <span className="text-xs text-white/80 font-medium">{formatViews(post.views ?? 0)}</span>
        </div>
        <div className="flex items-center gap-1.5 bg-black/30 backdrop-blur-sm rounded-full px-2.5 py-1 border border-white/15">
          <i className="ri-heart-line text-white/70 text-xs"></i>
          <span className="text-xs text-white/80 font-medium">{post.likes ?? 0}</span>
        </div>
        <div className="flex items-center gap-1.5 bg-black/30 backdrop-blur-sm rounded-full px-2.5 py-1 border border-white/15">
          <i className="ri-time-line text-white/70 text-xs"></i>
          <span className="text-xs text-white/80 font-medium">{post.readTime} min</span>
        </div>
      </div>

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-7">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] font-semibold text-white bg-white/20 backdrop-blur-sm px-2.5 py-0.5 rounded-full uppercase tracking-wider border border-white/25">
            {post.category}
          </span>
          {post.featured && (
            <span className="text-[10px] font-semibold text-amber-300 bg-amber-500/20 backdrop-blur-sm px-2.5 py-0.5 rounded-full border border-amber-400/30">
              Featured
            </span>
          )}
        </div>
        <h2 className="text-2xl md:text-3xl font-extrabold text-white leading-tight mb-2 max-w-2xl drop-shadow-sm">
          {post.title}
        </h2>
        <p className="text-sm text-white/75 line-clamp-1 max-w-xl mb-4">{post.excerpt}</p>
        <div className="flex items-center gap-3">
          <img src={post.authorAvatar} alt={post.author} className="w-7 h-7 rounded-full object-cover object-top border-2 border-white/50" />
          <span className="text-xs text-white/80 font-medium">{post.author}</span>
          <span className="text-white/40 text-xs">·</span>
          <span className="text-xs text-white/60">{formatDate(post.publishedAt)}</span>
        </div>
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); prev(); }}
        className="absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-sm border border-white/25 text-white transition-all cursor-pointer opacity-0 group-hover:opacity-100"
      >
        <i className="ri-arrow-left-s-line text-lg"></i>
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); next(); }}
        className="absolute right-4 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-sm border border-white/25 text-white transition-all cursor-pointer opacity-0 group-hover:opacity-100"
      >
        <i className="ri-arrow-right-s-line text-lg"></i>
      </button>

      <div className="absolute bottom-4 right-6 flex items-center gap-1.5">
        {featured.map((_, i) => (
          <button
            key={i}
            onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
            className={`rounded-full transition-all cursor-pointer ${
              i === current ? 'w-5 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/40 hover:bg-white/70'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function BlogCard({ post, onClick }: { post: typeof blogPosts[0]; onClick: () => void }) {
  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const formatViews = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`;

  return (
    <article
      className="group bg-white/55 backdrop-blur-md rounded-2xl border border-white/80 overflow-hidden cursor-pointer hover:border-[#0088CC]/25 transition-all flex flex-col h-full"
      onClick={onClick}
    >
      {/* Cover image — fixed height */}
      <div className="w-full h-44 overflow-hidden shrink-0">
        <img
          src={post.coverImage}
          alt={post.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>

      <div className="p-4 flex flex-col flex-1">
        {/* Category badge */}
        <div className="flex items-center gap-2 mb-2.5 shrink-0">
          <span className="text-[10px] font-semibold text-[#0088CC] bg-[#0088CC]/10 px-2 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap">
            {post.category}
          </span>
        </div>

        {/* Title — grows to fill space */}
        <h3 className="text-sm font-bold text-slate-800 leading-snug mb-2 group-hover:text-[#0088CC] transition-colors line-clamp-2 grow">
          {post.title}
        </h3>

        {/* Excerpt — fixed 2 lines */}
        <p className="text-xs text-slate-500 line-clamp-2 mb-3 shrink-0">{post.excerpt}</p>

        {/* Tags — fixed 1 line with truncation */}
        <div className="flex gap-1 mb-3 shrink-0 overflow-hidden">
          {post.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-[9px] text-slate-400 bg-slate-100/80 border border-slate-200/60 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              #{tag}
            </span>
          ))}
        </div>

        {/* Footer row — always pinned at bottom */}
        <div className="flex items-center gap-2 shrink-0 pt-2 border-t border-slate-100/70">
          <img
            src={post.authorAvatar}
            alt={post.author}
            className="w-6 h-6 rounded-full object-cover object-top border border-white/80 shrink-0"
          />
          <span className="text-[10px] text-slate-500 font-medium truncate flex-1 min-w-0">{post.author}</span>
          <div className="flex items-center gap-2.5 shrink-0">
            {post.views !== undefined && (
              <span className="flex items-center gap-0.5 text-[10px] text-slate-400">
                <i className="ri-eye-line text-[11px]"></i>
                {formatViews(post.views)}
              </span>
            )}
            <span className="text-[10px] text-slate-400">{post.readTime}m</span>
          </div>
        </div>

        {/* Date */}
        <p className="text-[10px] text-slate-400 mt-1.5 shrink-0">{formatDate(post.publishedAt)}</p>
      </div>
    </article>
  );
}

export default function BlogsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  useSEO({
    title: 'Blog – Telegram Community Growth Guides & Rankings | TGDirectory',
    description: 'Read expert guides, community rankings, and growth tips for Telegram group owners. Learn how to grow, monetize, and manage your Telegram community.',
    keywords: 'Telegram community blog, Telegram group growth, Telegram community guide, Telegram marketing, community management',
    canonical: 'https://tgdirectory.app/blogs',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'TGDirectory Blog',
      url: 'https://tgdirectory.app/blogs',
      description: 'Guides, rankings, and insights for Telegram community builders.',
      publisher: { '@type': 'Organization', name: 'TGDirectory', url: 'https://tgdirectory.app/' },
    },
  });

  const getCatFromUrl = () => {
    const raw = searchParams.get('category') ?? 'All';
    // 支持直接的 category 名，或者 tag 名模糊匹配到已知分类
    if (blogCategories.includes(raw)) return raw;
    return 'All';
  };

  const [selectedCat, setSelectedCat] = useState(getCatFromUrl);

  // URL 变化时同步筛选（从 blog 详情页跳回来时）
  useEffect(() => {
    const cat = getCatFromUrl();
    setSelectedCat(cat);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // 切换分类时同步 URL
  const handleCatChange = (cat: string) => {
    setSelectedCat(cat);
    if (cat === 'All') {
      setSearchParams({});
    } else {
      setSearchParams({ category: cat });
    }
  };

  // 每个分类的文章数量
  const catCounts = useMemo(() => {
    const counts: Record<string, number> = { All: blogPosts.length };
    blogCategories.slice(1).forEach((cat) => {
      counts[cat] = blogPosts.filter((p) => p.category === cat).length;
    });
    return counts;
  }, []);

  const filtered = useMemo(() => {
    if (selectedCat === 'All') return blogPosts;
    return blogPosts.filter((p) => p.category === selectedCat);
  }, [selectedCat]);

  const displayPosts = selectedCat === 'All'
    ? filtered.filter((p) => !p.featured)
    : filtered;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f8fb] via-[#f8fafc] to-[#f3f7fa] text-slate-800 font-sans">
      <ScrollBatteryBar />
      <SiteHeader showBack />

      <main className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 py-7">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-slate-400 mb-5">
          <button onClick={() => navigate('/')} className="hover:text-slate-600 transition-colors cursor-pointer whitespace-nowrap">Home</button>
          <span>/</span>
          <span className="text-[#0088CC] font-medium">Blogs</span>
        </nav>

        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-800 mb-1 tracking-tight">TGDirectory Blog</h1>
            <p className="text-slate-400 text-sm leading-relaxed">Guides, rankings, and insights for Telegram community builders.</p>
          </div>
          {/* Search entry */}
          <button
            onClick={() => navigate('/search')}
            className="flex items-center gap-2.5 bg-white/60 hover:bg-white/85 backdrop-blur-md border border-[#b8d4ea]/60 hover:border-[#0088CC]/30 rounded-xl px-4 py-2.5 text-sm text-slate-400 hover:text-slate-600 transition-all cursor-pointer whitespace-nowrap shrink-0 w-full sm:w-56"
          >
            <i className="ri-search-line text-base shrink-0"></i>
            <span className="flex-1 text-left">Search articles...</span>
            <kbd className="hidden sm:inline-flex items-center gap-0.5 text-[10px] font-mono bg-slate-100/80 text-slate-400 px-1.5 py-0.5 rounded border border-slate-200/60">⌘K</kbd>
          </button>
        </div>

        {/* Pinned Featured Articles Banner */}
        {selectedCat === 'All' && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="flex items-center gap-1.5 text-xs font-bold text-[#0088CC] uppercase tracking-wider">
                <i className="ri-star-fill text-xs"></i>
                Editor&apos;s Picks
              </span>
              <div className="flex-1 h-px bg-gradient-to-r from-[#0088CC]/20 to-transparent"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {blogPosts.filter((p) => p.featured).concat(blogPosts.filter((p) => !p.featured).slice(0, 2)).slice(0, 4).map((post, idx) => {
                const formatViews = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`;
                const accentColors = ['from-[#0088CC]/12 border-[#0088CC]/20', 'from-amber-500/10 border-amber-400/20', 'from-emerald-500/10 border-emerald-400/20', 'from-rose-500/10 border-rose-400/20'];
                const iconColors = ['text-[#0088CC]', 'text-amber-500', 'text-emerald-500', 'text-rose-500'];
                return (
                  <div
                    key={post.id}
                    onClick={() => navigate(`/blog/${post.id}`)}
                    className={`group relative bg-gradient-to-br ${accentColors[idx]} to-white/60 backdrop-blur-md rounded-xl border overflow-hidden cursor-pointer transition-all hover:scale-[1.02] flex gap-3 p-3`}
                    style={{ animationDelay: `${idx * 60}ms` }}
                  >
                    <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0">
                      <img src={post.coverImage} alt={post.title} className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 mb-1">
                        <i className={`ri-bookmark-fill text-[10px] ${iconColors[idx]}`}></i>
                        <span className={`text-[9px] font-bold uppercase tracking-wider ${iconColors[idx]}`}>{post.category}</span>
                      </div>
                      <p className="text-xs font-bold text-slate-800 leading-snug line-clamp-2 group-hover:text-[#0088CC] transition-colors mb-1">{post.title}</p>
                      <div className="flex items-center gap-2 text-[9px] text-slate-400">
                        <span className="flex items-center gap-0.5">
                          <i className="ri-eye-line"></i>{formatViews(post.views ?? 0)}
                        </span>
                        <span>{post.readTime}m</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex gap-6 items-start">
          {/* Left: carousel + posts */}
          <div className="flex-1 min-w-0">
            {/* Featured Carousel */}
            {selectedCat === 'All' && (
              <FeaturedCarousel onNavigate={(id) => navigate(`/blog/${id}`)} />
            )}
            {/* Category filter */}
            <div className="flex flex-wrap gap-2 mb-5">
              {blogCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCatChange(cat)}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                    selectedCat === cat
                      ? 'bg-[#0088CC] border-[#0088CC] text-white font-semibold'
                      : 'bg-white/50 border-[#b8d4ea]/60 text-slate-500 hover:border-[#0088CC]/40 hover:text-[#0088CC] hover:bg-[#0088CC]/5'
                  }`}
                >
                  {cat}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium leading-none ${
                    selectedCat === cat
                      ? 'bg-white/25 text-white'
                      : 'bg-slate-100/80 text-slate-400'
                  }`}>
                    {catCounts[cat] ?? 0}
                  </span>
                </button>
              ))}
            </div>

            {/* Post grid — items-stretch ensures all cards in a row are same height */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5 items-stretch">
              {displayPosts.map((post) => (
                <BlogCard
                  key={post.id}
                  post={post}
                  onClick={() => navigate(`/blog/${post.id}`)}
                />
              ))}
            </div>

            {displayPosts.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 text-center bg-white/50 backdrop-blur-md rounded-2xl border border-white/80">
                <i className="ri-article-line text-3xl text-slate-300 mb-3"></i>
                <p className="text-slate-400 text-sm">No posts in this category yet.</p>
                <button onClick={() => handleCatChange('All')} className="mt-3 text-xs text-[#0088CC] hover:underline cursor-pointer">View all posts</button>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="hidden xl:flex flex-col w-64 shrink-0 sticky top-20 gap-4">
            {/* Newsletter */}
            <div className="bg-gradient-to-br from-[#e8f4fb] to-[#ddeaf5] backdrop-blur-md rounded-2xl border border-[#b8d4ea]/60 p-5">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#0088CC]/15 mb-3">
                <i className="ri-mail-send-line text-[#0088CC] text-base"></i>
              </div>
              <h3 className="text-sm font-bold text-slate-700 mb-1">Weekly Newsletter</h3>
              <p className="text-xs text-slate-400 mb-3">Get the best community insights every Monday.</p>
              <input
                type="email"
                placeholder="your@email.com"
                className="w-full bg-white/80 border border-[#b8d4ea]/60 text-xs text-slate-700 placeholder-slate-400 px-3 py-2 rounded-lg outline-none focus:border-[#0088CC]/40 mb-2"
              />
              <GlowButton className="w-full text-xs py-2 rounded-lg">Subscribe</GlowButton>
            </div>

            {/* Ad slot 1 */}
            <div className="bg-white/60 backdrop-blur-md rounded-2xl border border-white/80 overflow-hidden">
              <div className="px-3 py-1.5 border-b border-slate-100/80">
                <span className="text-[10px] font-semibold text-[#0088CC] uppercase tracking-wider">Advertisement</span>
              </div>
              <div className="w-full h-36 overflow-hidden">
                <img src={adSlots[0].image} alt="Ad" className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-3">
                <p className="text-xs font-bold text-slate-700 mb-0.5">{adSlots[0].title}</p>
                <p className="text-[10px] text-slate-400 mb-2">{adSlots[0].desc}</p>
                <GlowButton className="w-full text-[11px] py-1.5 rounded-lg">{adSlots[0].cta}</GlowButton>
              </div>
            </div>

            {/* Popular tags */}
            <div className="bg-[#ddeaf5]/75 backdrop-blur-xl rounded-2xl border border-[#b8d4ea]/60 p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center text-[#0088CC]"><i className="ri-price-tag-3-line text-sm"></i></span>
                Popular Topics
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {['Growth', 'Crypto', 'AI', 'Bots', 'Monetization', 'Moderation', 'Strategy', 'Tools', 'Trends'].map((tag) => {
                  const target = blogCategories.includes(tag) ? tag : 'All';
                  const isActive = selectedCat === target && target !== 'All';
                  return (
                    <button
                      key={tag}
                      onClick={() => handleCatChange(target)}
                      className={`text-[10px] px-2 py-1 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                        isActive
                          ? 'bg-[#0088CC]/15 text-[#0088CC] border-[#0088CC]/40 font-semibold'
                          : 'text-slate-500 hover:text-[#0088CC] bg-white/60 hover:bg-[#0088CC]/10 border-[#c5d9eb]/50 hover:border-[#0088CC]/30'
                      }`}
                    >
                      #{tag}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Ad slot 2 */}
            <div className="bg-white/60 backdrop-blur-md rounded-2xl border border-white/80 overflow-hidden">
              <div className="px-3 py-1.5 border-b border-slate-100/80">
                <span className="text-[10px] font-semibold text-[#0088CC] uppercase tracking-wider">Advertisement</span>
              </div>
              <div className="w-full h-36 overflow-hidden">
                <img src={adSlots[1].image} alt="Ad" className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-3">
                <p className="text-xs font-bold text-slate-700 mb-0.5">{adSlots[1].title}</p>
                <p className="text-[10px] text-slate-400 mb-2">{adSlots[1].desc}</p>
                <GlowButton className="w-full text-[11px] py-1.5 rounded-lg">{adSlots[1].cta}</GlowButton>
              </div>
            </div>

            {/* Recent posts */}
            <div className="bg-white/60 backdrop-blur-md rounded-2xl border border-white/80 p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3">Recent Posts</h3>
              <div className="flex flex-col gap-3">
                {blogPosts.slice(0, 4).map((post) => (
                  <div
                    key={post.id}
                    className="flex gap-2.5 cursor-pointer group"
                    onClick={() => navigate(`/blog/${post.id}`)}
                  >
                    <img src={post.coverImage} alt={post.title} className="w-12 h-12 rounded-lg object-cover object-top shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-700 group-hover:text-[#0088CC] transition-colors line-clamp-2 leading-snug">{post.title}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{post.readTime} min read</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Ad slot 3 */}
            <div className="bg-white/60 backdrop-blur-md rounded-2xl border border-white/80 overflow-hidden">
              <div className="px-3 py-1.5 border-b border-slate-100/80">
                <span className="text-[10px] font-semibold text-[#0088CC] uppercase tracking-wider">Advertisement</span>
              </div>
              <div className="w-full h-36 overflow-hidden">
                <img src={adSlots[2].image} alt="Ad" className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-3">
                <p className="text-xs font-bold text-slate-700 mb-0.5">{adSlots[2].title}</p>
                <p className="text-[10px] text-slate-400 mb-2">{adSlots[2].desc}</p>
                <GlowButton className="w-full text-[11px] py-1.5 rounded-lg">{adSlots[2].cta}</GlowButton>
              </div>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
